import { CommandResult, CustomForm, CustomFormActivityListing, CustomFormAnswer, CustomFormReportData, CustomFormSelection, CustomFormSelectionOverview, FileUploadCustomFormFieldBody, rng } from '../amorg-common'
import { customFormRoutes } from '../amorg-common';
import { CustomFormTemplate } from '../amorg-common/DTOs/CustomFormTemplate';
import { apiPath } from "./adminClientApi";
import { commandAuthWrapper, queryAuthWrapper, rawCommandAuthWrapper } from "./serverApiHelpers";

export const getCustomFormActivityList = async (): Promise<CustomFormActivityListing[]> =>
{
    const result =  await queryAuthWrapper(`${apiPath}/activities/customforms`) as CustomFormActivityListing[];
    return result.map(a => 
        ({
            ...a,
            start:new Date(a.start),
            end:new Date(a.end)
        })
    )
}

export const getCustomForm = async (
    id: string,
): Promise<CustomForm | null> =>
    queryAuthWrapper(`${apiPath}/customforms/${id}`);

export const getCustomFormByActivityId = async (
        activityId: string,
    ): Promise<CustomForm | null> =>
        queryAuthWrapper(`${apiPath}/customforms/activity/${activityId}`);

export const getCustomFormAnonymous = async (
    id: string,
    siteId:string
): Promise<CustomForm | null> =>
    queryAuthWrapper(`${apiPath}/customforms/${id}@${siteId}`);

export const getCustomFormPublic = async (
    key: string,
    siteId:string
): Promise<CustomForm | null> =>
    queryAuthWrapper(`${apiPath}/customforms/public/${key}@${siteId}`);

export const setCustomFormPublicLink = async (
        id: string,
        action:'create' | 'remove'
    ): Promise<CommandResult> =>
        commandAuthWrapper(`${apiPath}/customforms/${id}/publiclink`, 'PUT',
            { publicLinkKey : action === 'create' ? rng.getRandomId() : '' }
        );

export const getCustomFormAnswerList = async (
    id: string,
): Promise<CustomFormAnswer[]> => 
{
    const data:CustomFormAnswer[] = await queryAuthWrapper(`${apiPath}/customforms/${id}/answers`);
    return data.map((d:CustomFormAnswer) => {
        return { ...d, started:new Date(d.started), lastEdited: new Date(d.lastEdited) };
    });
}

export const getCustomFormAnswer = async (
    id: string,
    siteId:string
): Promise<CustomFormAnswer | null> =>
{
    const data:CustomFormAnswer | null = await queryAuthWrapper(`${apiPath}/customforms/answers/${id}@${siteId}`);
    if(data === null)
        return null;

    return { ...data, started:new Date(data.started), lastEdited: new Date(data.lastEdited) };
}

export const getCustomFormSelectionOverview = async (
    siteId:string,
    formId:string,
): Promise<CustomFormSelectionOverview> =>
    queryAuthWrapper(`${apiPath}/customforms/${formId}@${siteId}/selections`);


export const startCustomFormAnswer = async (
    customFormId: string,
    answerId:string,
): Promise<CommandResult> =>
    commandAuthWrapper(customFormRoutes.startAnswer.apiFetchRoute([customFormId]), 'POST',
        { answerId });

export const startCustomFormAnswerPublic = async (
        key: string,
        siteId:string,
        answerId:string,
    ): Promise<CommandResult> =>
        commandAuthWrapper(customFormRoutes.startAnswerPublic.apiFetchRoute([key, siteId]), 'POST',
            { answerId });
    
export const submitCustomFormAnswerFields = async (
    answerId: string,
    siteId: string,
    selections:CustomFormSelection[],
): Promise<CommandResult> =>
    commandAuthWrapper(customFormRoutes.submitAnswerFields.apiFetchRoute([answerId, siteId]), 'PUT', { selections });

export const submitCustomFormAnswerFieldVideoChunk = async (
    siteId: string,
    answerId: string,
    fieldId: string,
    body:FileUploadCustomFormFieldBody,
    chunk:Blob,
    abortSignal:AbortSignal
): Promise<CommandResult> => {
    const formData = new FormData();
    formData.append('upload_file', chunk);
    for(const key in body)
        formData.append(key, body[key]);

    return await rawCommandAuthWrapper(customFormRoutes.submitVideoAnswerField.apiFetchRoute([answerId, siteId, fieldId]),
        'POST', formData, {}, abortSignal);
}

export const deleteCustomFormAnswerFieldVideo = async (
    siteId: string,
    answerId: string,
    fieldId: string
): Promise<CommandResult> => {
    return await commandAuthWrapper(customFormRoutes.deleteVideoAnswerField.apiFetchRoute([answerId, siteId, fieldId]),
        'DELETE'
    );
}

export const deleteCustomFormAnswer = async (
    id: string
): Promise<CommandResult> =>
    commandAuthWrapper(`${apiPath}/customforms/answers/${id}`, 'DELETE');

export const generateCustomFormTestAnswers = async (
    formId:string,
    count:number,
): Promise<CommandResult> =>
    commandAuthWrapper(`${apiPath}/customforms/${formId}/testanswers`, 'PUT',
        { count });

export const getCustomFormReport = async (
    formId:string
):Promise<CustomFormReportData | null> =>
    queryAuthWrapper(`${apiPath}/customforms/${formId}/report`); 


export const deleteCustomFormReport = async (
    formId:string
):Promise<CommandResult> =>
    commandAuthWrapper(`${apiPath}/customforms/${formId}/report`, 'DELETE'); 


export const customFormsVideoSubmissionEnabled = async (
    siteId:string
):Promise<boolean> => 
    queryAuthWrapper(customFormRoutes.videoSubmissionEnabled.apiFetchRoute([siteId]));

export const createCustomForm = async (
    id:string,
    activityId:string,
    templateId:string
): Promise<CommandResult> =>
    commandAuthWrapper(customFormRoutes.createForm.apiFetchRoute([]), 'POST',
        { id, activityId, templateId });

export const getCustomFormTemplates = async ():Promise<CustomFormTemplate[]> =>
        queryAuthWrapper(customFormRoutes.getTemplates.apiFetchRoute([])); 